import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

export interface DropdownOption {
  value: string;
  label: string;
  /** Optional group header (rendered once per run of equal groups). */
  group?: string;
}

/**
 * Fully theme-styled dropdown (open and closed) — a button + custom option list,
 * so it matches the app theme instead of the OS-native `<select>` picker.
 */
export function Dropdown({
  value,
  options,
  onChange,
  placeholder = 'Kies…',
  size = 'md',
  minWidth,
  ariaLabel,
  label,
  block = false,
  disabled = false,
}: {
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  size?: 'sm' | 'md';
  minWidth?: number;
  ariaLabel?: string;
  label?: string;
  block?: boolean;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disabled && open) setOpen(false);
  }, [disabled, open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const sm = size === 'sm';
  // Small dropdowns mimic the filter "chip" look so they sit next to chip buttons:
  // filled blue when a value is picked, white with blue text otherwise.
  const chipActive = sm && !!selected;

  const trigger: CSSProperties = {
    display: block ? 'flex' : 'inline-flex',
    width: block ? '100%' : undefined,
    boxSizing: 'border-box',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    minWidth,
    fontFamily: 'inherit',
    fontSize: sm ? 12 : 14,
    fontWeight: sm ? 700 : 400,
    padding: sm ? '5px 11px' : '9px 12px',
    borderRadius: sm ? 'var(--radius-sm)' : 'var(--radius-md)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.7 : 1,
    boxShadow: open ? 'var(--shadow-focus)' : 'none',
    whiteSpace: 'nowrap',
    ...(sm
      ? {
          background: chipActive ? 'var(--color-primary)' : '#FFFFFF',
          color: chipActive ? '#FFFFFF' : 'var(--color-primary)',
          border: `1px solid ${chipActive || open ? 'var(--color-primary)' : '#C9BBA8'}`,
        }
      : {
          background: disabled ? 'var(--color-surface-sunken)' : 'var(--color-surface)',
          color: selected ? 'var(--color-text)' : 'var(--color-text-muted)',
          border: `1px solid ${open ? 'var(--color-primary)' : 'var(--color-border)'}`,
        }),
  };

  let lastGroup: string | undefined;

  const control = (
    <div ref={ref} style={{ position: 'relative', display: block ? 'block' : 'inline-block' }}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        style={trigger}
      >
        <span>{selected ? selected.label : placeholder}</span>
        <svg
          width="11"
          height="7"
          viewBox="0 0 12 8"
          style={{
            flex: 'none',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform .15s',
          }}
        >
          <path
            d="M1 1l5 5 5-5"
            stroke="currentColor"
            strokeWidth="1.8"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 40,
            minWidth: '100%',
            maxHeight: 300,
            overflowY: 'auto',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-lg)',
            padding: 4,
          }}
        >
          {options.map((o) => {
            const header = o.group && o.group !== lastGroup ? o.group : null;
            lastGroup = o.group;
            const sel = o.value === value;
            const hot = o.value === hover;
            return (
              <div key={o.value || '__empty'}>
                {header && (
                  <div
                    style={{
                      padding: '7px 10px 3px',
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    {header}
                  </div>
                )}
                <div
                  role="option"
                  aria-selected={sel}
                  onMouseEnter={() => setHover(o.value)}
                  onMouseLeave={() => setHover((h) => (h === o.value ? null : h))}
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                  style={{
                    padding: '7px 10px',
                    fontSize: 13,
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    background: sel
                      ? 'var(--color-primary-soft)'
                      : hot
                        ? 'var(--color-surface-sunken)'
                        : 'transparent',
                    color: sel ? 'var(--color-primary)' : 'var(--color-text)',
                    fontWeight: sel ? 700 : 400,
                  }}
                >
                  {o.label}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  if (!label) return control;
  return (
    <div className="vs-field">
      <span className="vs-label">{label}</span>
      {control}
    </div>
  );
}
